import { MenuTitles, Pages } from 'constants/menu'
import { ContractSubPage } from '../ContractSubPage'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { AddSpecialConditionButton } from './modals/specialConditions/AddSpecialConditionButton'
import { useSelectedContract } from 'hooks'
import {
  RenderedSpecialCondition,
  RenderedSpecialConditionConditionType,
  SpecialConditionData,
  useConstructConditionClauseText,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import { useCallback, useEffect, useState } from 'react'
import { isDirtyEqual } from 'utils/compares'
import ErrorModal from 'helpers/ErrorModal'
import LoadingState from 'components/loadingState/LoadingState'
import { AgxEmptyState, useToast } from '@urbanx/agx.ui-components2'
import SpecialConditionCollapsiblePanel from './panels/SpecialConditionCollapsiblePanel'

const SpecialConditions = () => {
  const { contract, campaignId } = useSelectedContract()
  const [, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors] = useState<string[]>([])

  const { data: specialConditionsResponse, isPending } =
    useStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '')
  const invalidateStaffGetSpecialConditions =
    useInvalidateUseStaffGetSpecialConditions()

  const [localSpecialConditions, setLocalSpecialConditions] = useState<
    SpecialConditionData[] | undefined
  >(specialConditionsResponse?.specialConditionsData)

  const { pushToast } = useToast()

  const { constructConditionClauseText, constructConditionClauseTextPending } =
    useConstructConditionClauseText({
      onSuccess: (conditionTextBlocks) => {
        setRenderedSpecialConditions(conditionTextBlocks)
      },
      onError: async () => {
        pushToast({
          message: 'Failed to construct condition text blocks',
          type: 'error',
        })
        await invalidateStaffGetSpecialConditions(
          campaignId ?? '',
          contract?.contractId ?? ''
        )
      },
    })

  useEffect(() => {
    if (!localSpecialConditions) return

    constructConditionClauseText({
      campaignId: campaignId ?? '',
      contractId: contract?.contractId ?? '',
      model: localSpecialConditions,
    })
  }, [])

  const [renderedSpecialConditions, setRenderedSpecialConditions] = useState<
    RenderedSpecialCondition[] | undefined
  >(specialConditionsResponse?.renderedConditions)

  useEffect(() => {
    const dirtyEquals = !isDirtyEqual(
      localSpecialConditions,
      specialConditionsResponse?.specialConditionsData
    )

    setValidate(dirtyEquals)

    if (!dirtyEquals) return

    setLocalSpecialConditions(specialConditionsResponse?.specialConditionsData)
    setRenderedSpecialConditions(specialConditionsResponse?.renderedConditions)
  }, [specialConditionsResponse])

  useEffect(() => {
    return () => {
      if (!campaignId || !contract?.contractId) return

      void invalidateStaffGetSpecialConditions(campaignId, contract.contractId)
    }
  }, [campaignId, contract?.contractId, invalidateStaffGetSpecialConditions])

  const { staffUpdateContractSpecialConditions } =
    useStaffUpdateContractSpecialConditions({
      onSuccess: () => {},
      onError: async () => {
        pushToast({
          message: 'Failed to update special conditions',
          type: 'error',
        })
        await invalidateStaffGetSpecialConditions(
          campaignId ?? '',
          contract?.contractId ?? ''
        )
      },
    })

  const onConditionDeleted = useCallback(
    async (conditionId: string) => {
      if (!conditionId) return

      const updatedConditions =
        localSpecialConditions?.filter(
          (condition) => condition.id !== conditionId
        ) ?? []

      setLocalSpecialConditions(updatedConditions)

      await staffUpdateContractSpecialConditions({
        contractId: contract?.contractId ?? '',
        body: updatedConditions,
      })

      await constructConditionClauseText({
        campaignId: campaignId ?? '',
        contractId: contract?.contractId ?? '',
        model: updatedConditions,
      })
    },
    [localSpecialConditions, contract?.contractId, campaignId]
  )

  const onAddCustomCondition = useCallback(
    async (customSpecialCondition: SpecialConditionData) => {
      const updatedConditions = [
        ...(localSpecialConditions || []),
        customSpecialCondition,
      ]

      setLocalSpecialConditions((prev) => [
        ...(prev || []),
        customSpecialCondition,
      ])

      setRenderedSpecialConditions((prev) => [
        ...(prev || []),
        {
          id: customSpecialCondition.id,
          conditionType: RenderedSpecialConditionConditionType.Custom,
          position: ((localSpecialConditions?.length ?? 0) + 1).toString(),
          title: '...',
          clauses: [],
        },
      ])

      await staffUpdateContractSpecialConditions({
        contractId: contract?.contractId ?? '',
        body: updatedConditions,
      })

      await constructConditionClauseText({
        campaignId: campaignId ?? '',
        contractId: contract?.contractId ?? '',
        model: updatedConditions,
      })
    },
    [localSpecialConditions, campaignId, contract?.contractId]
  )

  const onDueDateUpdated = useCallback(
    (conditionId: string, clauseId: string, newDueDate: Date | undefined) => {
      setLocalSpecialConditions((prevConditions) =>
        prevConditions?.map((condition) => {
          if (condition.id !== conditionId) return condition

          return {
            ...condition,
            clauses: condition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause

              return {
                ...clause,
                overriddenDueDate: {
                  referenceDate: newDueDate,
                },
              }
            }),
          }
        })
      )

      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.map((renderedCondition) => {
          if (renderedCondition.id !== conditionId) return renderedCondition

          return {
            ...renderedCondition,
            clauses: renderedCondition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause

              return {
                ...clause,
                dueDate: newDueDate,
              }
            }),
          }
        })
      )
    },
    []
  )

  if (
    (isPending || constructConditionClauseTextPending) &&
    !renderedSpecialConditions
  ) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.SPECIAL_CONDITIONS}
      subPages={getContractInformationSubPages()}
      onSaveChanges={() => {}}
      isFormDirty={false}
      isSaving={false}
      isError={false}
      hideSaveButton
    >
      {!renderedSpecialConditions || renderedSpecialConditions.length === 0 ? (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      ) : null}

      {renderedSpecialConditions?.map((renderedCondition) => (
        <SpecialConditionCollapsiblePanel
          renderedSpecialCondition={renderedCondition}
          onDeleteCondition={onConditionDeleted}
          key={renderedCondition.id ?? renderedCondition.conditionType}
          campaignId={campaignId ?? ''}
          contractId={contract?.contractId ?? ''}
          onDueDateUpdated={onDueDateUpdated}
        />
      ))}
      <AddSpecialConditionButton
        key="add-special-condition-button"
        id="add-special-condition-button"
        label="Add Special Conditions"
        existingConditions={localSpecialConditions || []}
        onAddSpecialCondition={onAddCustomCondition}
        campaignId={campaignId ?? ''}
      />
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
    </ContractSubPage>
  )
}

export default SpecialConditions
